import { useEffect, useMemo, useRef, useState } from "react"
import { debounce, map } from "lodash"
import { Accordion, AccordionDetails, AccordionSummary, Box, Divider, IconButton, Menu, MenuItem, Tooltip, Typography } from "@mui/material"
import { AddCircleOutline, ArrowDownward, ArrowUpward, Close, ExpandMore } from "@mui/icons-material"
import SectionHeading from "./SectionHeading"
import NoteCreator from "./NoteCreator"
import NotesList from "./NotesList"
import MenuIcon from '@mui/icons-material/Menu'
import React from "react"
import { DEFAULT_SECTION, NoteSectionType } from "../types"
import { arrayLength } from "@/utils/helperFunctions"

interface NotesSectionProps {
  noteSections: NoteSectionType[];
  setNoteSections: React.Dispatch<React.SetStateAction<NoteSectionType[]>>;
  updateNotes?: (sections: NoteSectionType[]) => void;
}

const NotesSection: React.FC<NotesSectionProps> = ({ noteSections, setNoteSections, updateNotes = () => { } }) => {
  const [menuState, setMenuState] = useState<{ index: number | null; anchorEl: HTMLElement | null }>({
    index: null,
    anchorEl: null,
  });
  const [hoveredGap, setHoveredGap] = useState<number | null>(null);
  const [flashIndex, setFlashIndex] = useState<number | null>(null);
  const flashTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const closeMenu = () => setMenuState({ index: null, anchorEl: null });

  // Always call the latest updateNotes without resetting the debounce timer on re-renders
  const updateNotesRef = useRef(updateNotes);
  useEffect(() => { updateNotesRef.current = updateNotes; }, [updateNotes]);

  const debouncedUpdateNotes = useMemo(
    () => debounce((sections: NoteSectionType[]) => updateNotesRef.current(sections), 600),
    []
  );
  useEffect(() => () => debouncedUpdateNotes.cancel(), [debouncedUpdateNotes]);

  const handleInsertSection = (afterIndex: number) => {
    const updated = [
      ...noteSections.slice(0, afterIndex + 1),
      { ...DEFAULT_SECTION },
      ...noteSections.slice(afterIndex + 1),
    ];
    setNoteSections(updated);
    updateNotes(updated);
  };

  const handleMoveSection = (index: number, direction: 'up' | 'down') => {
    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    const updated = [...noteSections];
    [updated[index], updated[swapIndex]] = [updated[swapIndex], updated[index]];
    setNoteSections(updated);
    debouncedUpdateNotes(updated);

    if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
    setFlashIndex(swapIndex);
    flashTimerRef.current = setTimeout(() => setFlashIndex(null), 700);
  };

  const handleTitleChange = (index: number, newTitle: string) => {
    const updated = [...noteSections];
    updated[index] = { sectionName: newTitle, sectionNotes: updated[index].sectionNotes };
    setNoteSections(updated);
    updateNotes(updated);
  }

  const handleNewNote = (index: number, newNote: string) => {
    const updated = [...noteSections];
    updated[index] = { sectionName: updated[index].sectionName, sectionNotes: [...updated[index].sectionNotes, newNote] };
    setNoteSections(updated);
    updateNotes(updated);
  }

  const handleNotesUpdate = (index: number, updatedNotes: string[]) => {
    const updated = [...noteSections];
    updated[index] = { sectionName: updated[index].sectionName, sectionNotes: updatedNotes };
    setNoteSections(updated);
    updateNotes(updated);
  }

  const handleSectionDeletion = (index: number) => {
    closeMenu();
    const updated = arrayLength(noteSections) === 1 ? [] : noteSections.filter((_, i) => i !== index);
    setNoteSections(updated);
    updateNotes(updated);
  }

  return (
    <React.Fragment>
      {map(noteSections, (section, index) =>
        <React.Fragment key={index}>
          <Accordion
            defaultExpanded
            disableGutters
            sx={{ border: '1px solid darkgray', '&:before': { display: 'none' } }}
            data-testid={`notes-section-${index}`}
          >
            <AccordionSummary
              expandIcon={<ExpandMore />}
              component="div"
              sx={{
                flexDirection: 'row-reverse',
                gap: 1,
                bgcolor: flashIndex === index ? 'primary.light' : 'inherit',
                transition: 'background-color 0.6s ease',
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }} onClick={(e) => e.stopPropagation()}>
                <SectionHeading
                  sectionName={section.sectionName}
                  handleTitleChange={(newTitle: string) => handleTitleChange(index, newTitle)}
                />
              </Box>
              <Tooltip title="Move up">
                <span>
                  <IconButton
                    size="small"
                    disabled={index === 0}
                    onClick={(e) => { e.stopPropagation(); handleMoveSection(index, 'up'); }}
                  >
                    <ArrowUpward fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Move down">
                <span>
                  <IconButton
                    size="small"
                    disabled={index === arrayLength(noteSections) - 1}
                    onClick={(e) => { e.stopPropagation(); handleMoveSection(index, 'down'); }}
                  >
                    <ArrowDownward fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <IconButton
                size="small"
                onClick={(event) => { event.stopPropagation(); setMenuState({ index, anchorEl: event.currentTarget }); }}
              >
                <MenuIcon fontSize="small" />
              </IconButton>
              <Menu
                anchorEl={menuState.anchorEl}
                open={menuState.index === index}
                onClose={closeMenu}
              >
                <MenuItem>
                  <Tooltip title="Delete Section">
                    <Close onClick={() => handleSectionDeletion(index)} />
                  </Tooltip>
                </MenuItem>
              </Menu>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ mb: 3 }}>
                <NoteCreator
                  handleNewNote={(newTitle: string) => handleNewNote(index, newTitle)}
                />
              </Box>
              <NotesList
                notes={section.sectionNotes}
                sectionName={section.sectionName}
                handleNotesUpdate={(updatedNotes: string[]) => handleNotesUpdate(index, updatedNotes)}
              />
            </AccordionDetails>
          </Accordion>

          {index < arrayLength(noteSections) - 1 && (
            <Box
              onMouseEnter={() => setHoveredGap(index)}
              onMouseLeave={() => setHoveredGap(null)}
              sx={{ position: 'relative', height: 16, display: 'flex', alignItems: 'center', cursor: 'pointer' }}
              onClick={() => handleInsertSection(index)}
            >
              <Divider sx={{ flex: 1, borderColor: hoveredGap === index ? 'primary.main' : 'transparent', transition: 'border-color 0.15s' }} />
              {hoveredGap === index && (
                <Box sx={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', display: 'flex', alignItems: 'center', gap: 0.5, bgcolor: 'background.paper', px: 1, color: 'primary.main' }}>
                  <AddCircleOutline sx={{ fontSize: 16 }} />
                  <Typography variant="caption">Add section</Typography>
                </Box>
              )}
            </Box>
          )}
        </React.Fragment>
      )}
    </React.Fragment>
  )
}

export default NotesSection;
